import { useState, useCallback, useRef } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Sheet, SheetHeader, SheetBody } from "./Sheet";
import { Badge } from "./Badge";
import { OwnerTag } from "./OwnerTag";
import {
  IconColorPalette,
  IconDocFolder,
  IconFiles,
  IconPen,
  IconImage,
  IconFolder,
  IconFileDownload,
  IconFileUpload,
  IconDeleteX,
} from "nucleo-glass";
import { cn } from "../../lib/utils";
import {
  ASSET_CATEGORIES,
  isPreviewableImage,
  isPreviewablePdf,
  formatFileSize,
  formatRelativeTime,
  getCategoryLabel,
} from "../../lib/assetUtils";
import type { AssetCategory } from "../../lib/assetUtils";
import type { Owner } from "../../data/types";
import type { Id } from "../../../convex/_generated/dataModel";
import type { ReactNode } from "react";
import { OWNER_OPTIONS } from "../../lib/owner";

const categoryIconsLg: Record<AssetCategory, () => ReactNode> = {
  branding: () => <IconColorPalette size={48} />,
  documents: () => <IconDocFolder size={48} />,
  decks: () => <IconFiles size={48} />,
  notes: () => <IconPen size={48} />,
  images: () => <IconImage size={48} />,
  other: () => <IconFolder size={48} />,
};

// ── Props ───────────────────────────────────────────────────────

interface AssetDetailSheetProps {
  asset: {
    id: string;
    title: string;
    description: string;
    category: AssetCategory;
    owner: Owner;
    storageId?: string;
    fileName?: string;
    fileType?: string;
    fileSize?: number;
    fileUrl?: string | null;
    currentVersion?: number;
    createdAt: number;
  } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: (id: string, fields: {
    title?: string;
    description?: string;
    category?: AssetCategory;
    owner?: Owner;
  }) => Promise<void>;
  onReplaceFile: (assetId: string, file: File, uploadedBy: Owner) => Promise<void>;
  onRestoreVersion: (assetId: string, versionNumber: number) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onDownload: (id: string) => void;
}

export function AssetDetailSheet({
  asset,
  open,
  onOpenChange,
  onUpdate,
  onReplaceFile,
  onRestoreVersion,
  onDelete,
  onDownload,
}: AssetDetailSheetProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editCategory, setEditCategory] = useState<AssetCategory>("documents");
  const [editOwner, setEditOwner] = useState<Owner>("Joel");
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch version history
  const versions = useQuery(
    api.assetVersions.listByAsset,
    asset ? { assetId: asset.id as Id<"assets"> } : "skip",
  );

  const startEdit = useCallback(() => {
    if (!asset) return;
    setEditTitle(asset.title);
    setEditDescription(asset.description);
    setEditCategory(asset.category);
    setEditOwner(asset.owner);
    setIsEditing(true);
  }, [asset]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const saveEdit = useCallback(async () => {
    if (!asset) return;
    await onUpdate(asset.id, {
      title: editTitle.trim(),
      description: editDescription.trim(),
      category: editCategory,
      owner: editOwner,
    });
    setIsEditing(false);
  }, [asset, editTitle, editDescription, editCategory, editOwner, onUpdate]);

  const handleReplaceFile = useCallback(async (file: File) => {
    if (!asset) return;
    setIsReplacing(true);
    try {
      await onReplaceFile(asset.id, file, asset.owner);
    } finally {
      setIsReplacing(false);
    }
  }, [asset, onReplaceFile]);

  const handleDelete = useCallback(async () => {
    if (!asset) return;
    setIsDeleting(true);
    try {
      await onDelete(asset.id);
      onOpenChange(false);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [asset, onDelete, onOpenChange]);

  if (!asset) return null;

  const hasPreviewableImage = isPreviewableImage(asset.fileType) && asset.fileUrl;
  const hasPreviewablePdf = isPreviewablePdf(asset.fileType) && asset.fileUrl;
  const CategoryIcon = categoryIconsLg[asset.category];

  return (
    <Sheet open={open} onOpenChange={onOpenChange} title={asset.title}>
      <SheetHeader onClose={() => onOpenChange(false)}>
        <div className="flex items-center gap-[var(--spacing-2)] min-w-0">
          <span className="text-[14px] font-semibold text-[var(--color-text-primary)] truncate">
            {asset.title}
          </span>
          {asset.currentVersion !== undefined && asset.currentVersion > 0 && (
            <span className="text-[11px] text-[var(--color-text-muted)] tabular-nums shrink-0">
              v{asset.currentVersion}
            </span>
          )}
        </div>
      </SheetHeader>

      <SheetBody>
        <div className="flex flex-col gap-[var(--spacing-6)]">
          {/* ── Preview ── */}
          <div className="rounded-[var(--radius-md)] border border-[var(--color-border-stroke)] overflow-hidden bg-[var(--color-bg-hover)]">
            {hasPreviewableImage ? (
              <img src={asset.fileUrl!} alt={asset.title} className="w-full max-h-[280px] object-contain" />
            ) : hasPreviewablePdf ? (
              <object
                data={`${asset.fileUrl!}#toolbar=1&navpanes=0`}
                type="application/pdf"
                className="w-full h-[400px]"
                aria-label={`PDF viewer: ${asset.title}`}
              >
                <div className="flex flex-col items-center justify-center h-full gap-[var(--spacing-2)] py-[var(--spacing-6)]">
                  <CategoryIcon />
                  <a
                    href={asset.fileUrl!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[13px] text-[var(--color-accent)] hover:underline"
                  >
                    Open PDF in new tab
                  </a>
                </div>
              </object>
            ) : (
              <div className="flex items-center justify-center py-[var(--spacing-12)] text-[var(--color-accent-300)] opacity-40">
                <CategoryIcon />
              </div>
            )}
          </div>

          {/* ── Actions row ── */}
          <div className="flex items-center gap-[var(--spacing-2)]">
            {asset.fileUrl && (
              <button
                onClick={() => onDownload(asset.id)}
                className={cn(
                  "flex items-center gap-[var(--spacing-1_5)] h-[32px] px-[var(--spacing-3)]",
                  "rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
                  "bg-[var(--color-bg-surface)] text-[13px] font-medium text-[var(--color-text-secondary)]",
                  "hover:bg-[var(--color-bg-hover)] transition-colors duration-[120ms] cursor-pointer",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
                )}
              >
                <IconFileDownload size={14} />
                Download
              </button>
            )}

            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isReplacing}
              className={cn(
                "flex items-center gap-[var(--spacing-1_5)] h-[32px] px-[var(--spacing-3)]",
                "rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
                "bg-[var(--color-bg-surface)] text-[13px] font-medium text-[var(--color-text-secondary)]",
                "hover:bg-[var(--color-bg-hover)] transition-colors duration-[120ms] cursor-pointer",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
              )}
            >
              <IconFileUpload size={14} />
              {isReplacing ? "Uploading…" : asset.storageId ? "Replace file" : "Upload file"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleReplaceFile(f);
              }}
            />

            {!isEditing && (
              <button
                onClick={startEdit}
                className={cn(
                  "h-[32px] px-[var(--spacing-3)]",
                  "rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
                  "bg-[var(--color-bg-surface)] text-[13px] font-medium text-[var(--color-text-secondary)]",
                  "hover:bg-[var(--color-bg-hover)] transition-colors duration-[120ms] cursor-pointer",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
                )}
              >
                Edit
              </button>
            )}

            <div className="ml-auto">
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className={cn(
                    "h-[32px] px-[var(--spacing-3)]",
                    "rounded-[var(--radius-md)] text-[13px] font-medium",
                    "text-[var(--color-state-error)] hover:bg-red-500/10",
                    "transition-colors duration-[120ms] cursor-pointer",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
                  )}
                >
                  <IconDeleteX size={14} />
                </button>
              ) : (
                <div className="flex items-center gap-[var(--spacing-1)]">
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className={cn(
                      "h-[32px] px-[var(--spacing-3)] rounded-[var(--radius-md)]",
                      "text-[13px] font-medium text-white bg-red-600 hover:bg-red-500",
                      "disabled:opacity-50 transition-colors duration-[120ms] cursor-pointer",
                    )}
                  >
                    {isDeleting ? "Deleting…" : "Confirm"}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="h-[32px] px-[var(--spacing-2)] text-[13px] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Edit form ── */}
          {isEditing ? (
            <div className="flex flex-col gap-[var(--spacing-3)] p-[var(--spacing-3)] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-canvas)]">
              <label className="flex flex-col gap-[var(--spacing-1)]">
                <span className="text-[12px] font-medium text-[var(--color-text-muted)]">Title</span>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className={cn(
                    "h-[32px] rounded-[var(--radius-sm)] border border-[var(--color-border-stroke)]",
                    "bg-[var(--color-bg-surface)] px-[var(--spacing-2)] text-[14px] text-[var(--color-text-primary)]",
                    "focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-focus)]",
                  )}
                />
              </label>
              <label className="flex flex-col gap-[var(--spacing-1)]">
                <span className="text-[12px] font-medium text-[var(--color-text-muted)]">Description</span>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={2}
                  className={cn(
                    "rounded-[var(--radius-sm)] border border-[var(--color-border-stroke)]",
                    "bg-[var(--color-bg-surface)] px-[var(--spacing-2)] py-[var(--spacing-1_5)] text-[14px] text-[var(--color-text-primary)] resize-none",
                    "focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-focus)]",
                  )}
                />
              </label>
              <div className="flex gap-[var(--spacing-2)]">
                <label className="flex flex-col gap-[var(--spacing-1)] flex-1">
                  <span className="text-[12px] font-medium text-[var(--color-text-muted)]">Category</span>
                  <select
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value as AssetCategory)}
                    className={cn(
                      "h-[32px] rounded-[var(--radius-sm)] border border-[var(--color-border-stroke)]",
                      "bg-[var(--color-bg-surface)] px-[var(--spacing-2)] text-[14px] text-[var(--color-text-primary)]",
                      "focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-focus)] cursor-pointer",
                    )}
                  >
                    {ASSET_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-[var(--spacing-1)] flex-1">
                  <span className="text-[12px] font-medium text-[var(--color-text-muted)]">Owner</span>
                  <select
                    value={editOwner}
                    onChange={(e) => setEditOwner(e.target.value as Owner)}
                    className={cn(
                      "h-[32px] rounded-[var(--radius-sm)] border border-[var(--color-border-stroke)]",
                      "bg-[var(--color-bg-surface)] px-[var(--spacing-2)] text-[14px] text-[var(--color-text-primary)]",
                      "focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-focus)] cursor-pointer",
                    )}
                  >
                    {OWNER_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="flex justify-end gap-[var(--spacing-2)] pt-[var(--spacing-1)]">
                <button
                  onClick={cancelEdit}
                  className="h-[32px] px-[var(--spacing-3)] text-[13px] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] rounded-[var(--radius-sm)] cursor-pointer transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveEdit}
                  disabled={!editTitle.trim()}
                  className={cn(
                    "h-[32px] px-[var(--spacing-3)] text-[13px] font-medium text-white rounded-[var(--radius-sm)]",
                    "bg-gradient-to-b from-[var(--color-accent-400)] to-[var(--color-accent-600)]",
                    "hover:from-[var(--color-accent-300)] hover:to-[var(--color-accent-500)]",
                    "disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-all",
                  )}
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            /* ── Info display ── */
            <div className="flex flex-col gap-[var(--spacing-3)]">
              {asset.description && (
                <p className="text-[14px] text-[var(--color-text-secondary)] leading-[20px]">{asset.description}</p>
              )}
              <div className="flex items-center gap-[var(--spacing-2)] flex-wrap">
                <Badge>{getCategoryLabel(asset.category)}</Badge>
                <OwnerTag owner={asset.owner} />
                {asset.fileName && (
                  <span className="text-[12px] text-[var(--color-text-muted)]">{asset.fileName}</span>
                )}
                {asset.fileSize ? (
                  <span className="text-[12px] text-[var(--color-text-muted)] tabular-nums">{formatFileSize(asset.fileSize)}</span>
                ) : null}
                <span className="text-[12px] text-[var(--color-text-muted)] tabular-nums">{formatRelativeTime(asset.createdAt)}</span>
              </div>
            </div>
          )}

          {/* ── Version History ── */}
          {versions && versions.length > 1 && (
            <div className="flex flex-col gap-[var(--spacing-2)]">
              <button
                onClick={() => setShowVersionHistory((p) => !p)}
                className={cn(
                  "flex items-center gap-[var(--spacing-1_5)] text-[13px] font-medium text-[var(--color-text-muted)]",
                  "hover:text-[var(--color-text-primary)] transition-colors duration-[120ms] cursor-pointer",
                )}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  className={cn("transition-transform duration-[120ms]", showVersionHistory && "rotate-90")}
                >
                  <path d="M4.5 2.5L8 6L4.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
                History ({versions.length} versions)
              </button>

              {showVersionHistory && (
                <div className="flex flex-col gap-[var(--spacing-1)] ml-[var(--spacing-3)]">
                  {versions.map((version) => {
                    const isCurrent = version.versionNumber === asset.currentVersion;
                    return (
                      <div
                        key={version._id}
                        className={cn(
                          "flex items-center gap-[var(--spacing-2)] px-[var(--spacing-2)] py-[var(--spacing-1_5)]",
                          "rounded-[var(--radius-sm)]",
                          isCurrent && "bg-[var(--color-bg-hover)]",
                        )}
                      >
                        {/* Timeline dot */}
                        <div className={cn(
                          "w-[6px] h-[6px] rounded-full shrink-0",
                          isCurrent ? "bg-[var(--color-accent)]" : "bg-[var(--color-border-strong)]",
                        )} />

                        <span className={cn(
                          "text-[13px] tabular-nums shrink-0",
                          isCurrent ? "font-medium text-[var(--color-text-primary)]" : "text-[var(--color-text-muted)]",
                        )}>
                          v{version.versionNumber}
                          {isCurrent && " (current)"}
                        </span>

                        <OwnerTag owner={version.uploadedBy} className="scale-90 origin-left" />

                        <span className="text-[12px] text-[var(--color-text-muted)] tabular-nums">
                          {formatRelativeTime(version._creationTime)}
                        </span>

                        <span className="text-[11px] text-[var(--color-text-muted)] tabular-nums">
                          {formatFileSize(version.fileSize)}
                        </span>

                        <div className="ml-auto flex items-center gap-[var(--spacing-1)]">
                          {version.fileUrl && (
                            <a
                              href={version.fileUrl}
                              download={version.fileName}
                              onClick={(e) => e.stopPropagation()}
                              className="p-[2px] text-[var(--color-icon)] hover:text-[var(--color-text-primary)] transition-colors"
                              aria-label={`Download v${version.versionNumber}`}
                            >
                              <IconFileDownload size={12} />
                            </a>
                          )}
                          {!isCurrent && (
                            <button
                              onClick={() => onRestoreVersion(asset.id, version.versionNumber)}
                              className={cn(
                                "text-[11px] font-medium text-[var(--color-accent)] hover:text-[var(--color-accent-300)]",
                                "transition-colors duration-[120ms] cursor-pointer",
                              )}
                            >
                              Restore
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </SheetBody>
    </Sheet>
  );
}
